import React from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { HomeThreadCard } from '../components/CommentsSection';
import { useUser } from '../context/UserContext';
import { pick, homeText } from '../i18n';

export default function Home({ lang = 'en' }) {
  const { user } = useUser();
  const name = user?.name || '';

  const [stats, setStats] = React.useState(null);
  const [nextDate, setNextDate] = React.useState(null);
  const [topWeek, setTopWeek] = React.useState([]);
  const [topAllTime, setTopAllTime] = React.useState([]);
  const [recentComments, setRecentComments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const navigate = useNavigate();

  const year = new Date().getFullYear();
  const yearStart = `${year}-01-01`;
  const yearEnd   = `${year}-12-31`;

  const t = pick(homeText, lang);

  React.useEffect(() => { loadLeaderboard(); loadRecentComments(); }, []);

  // Re-run stats whenever the logged-in user changes
  React.useEffect(() => {
    setStats(null);
    setNextDate(null);
    if (name) loadMyStats(name, user?.id);
  }, [name, user?.id]);

  async function loadRecentComments() {
    // Newest first, so the limit trims the oldest comments rather than the ones
    // we want. Flipped back to chronological below, which is the order threads
    // are displayed in.
    const { data: newest } = await supabase
      .from('comments')
      .select('id,name,text,date,created_at,reactions,parent_id')
      .order('created_at', { ascending: false })
      .limit(300);
    if (!newest) return;
    const data = [...newest].reverse();

    // A recent reply can belong to a thread whose root (or the reply it answers)
    // is older than the window. Fetch those by id so the thread still groups
    // under its root. Two rounds covers a reply to a reply to the root.
    for (let round = 0; round < 2; round++) {
      const have = new Set(data.map(c => c.id));
      const missing = [...new Set(data.map(c => c.parent_id).filter(id => id && !have.has(id)))];
      if (!missing.length) break;
      const { data: parents } = await supabase
        .from('comments')
        .select('id,name,text,date,created_at,reactions,parent_id')
        .in('id', missing);
      if (!parents || !parents.length) break;
      data.unshift(...parents);
    }

    const threadMap = {};
    const rootMap = {};
    data.forEach(c => { if (!c.parent_id) rootMap[c.id] = c; });
    data.forEach(c => {
      const rootId = c.parent_id
        ? (rootMap[c.parent_id] ? c.parent_id : data.find(x => x.id === c.parent_id)?.parent_id || c.parent_id)
        : c.id;
      if (!threadMap[rootId]) threadMap[rootId] = [];
      threadMap[rootId].push(c);
    });

    const threads = Object.entries(threadMap).map(([rootId, comments]) => {
      const latest = Math.max(...comments.map(c => new Date(c.created_at).getTime()));
      return { rootId, comments, latest };
    }).sort((a, b) => b.latest - a.latest).slice(0, 5);

    setRecentComments(threads);
  }

  async function loadMyStats(n, id) {
    // Matched by account, so check-ins made under an old name still count; rows
    // from before accounts existed are matched by name (same rule as Schedule.js).
    const { data: rows, error } = await supabase.rpc('get_reader_stats', {
      p_user_id: id || null, p_name: n, date_from: yearStart, date_to: yearEnd,
    });
    if (error || !rows || !rows.length) return;

    const byDate = {};
    rows.forEach(r => { byDate[r.date] = { nt: r.has_nt, ot: r.has_ot }; });

    const fullDays  = Object.values(byDate).filter(d => d.nt && d.ot).length;
    const totalDays = Object.keys(byDate).length;

    let streak = 0;
const today = new Date(); // keep this — used below for nextDate too
const todayKey = today.toLocaleDateString('en-CA');
// Count consecutive read days ending today. Not having read TODAY yet doesn't
// break the streak (the day isn't over) — only a missed past day does.
for (let i = 0; i < 400; i++) {
  const expected = new Date(todayKey + 'T12:00:00');
  expected.setDate(expected.getDate() - i);
  const expectedKey = expected.toLocaleDateString('en-CA');
  const read = byDate[expectedKey] && (byDate[expectedKey].nt || byDate[expectedKey].ot);
  if (read) streak++;
  else if (i === 0) continue; // today still pending
  else break;
}

    const readDates = Object.keys(byDate).sort();
    if (readDates.length > 0) {
      const lastRead = readDates[readDates.length - 1];
      const nextDay = new Date(lastRead + 'T12:00:00');
      nextDay.setDate(nextDay.getDate() + 1);
      setNextDate(nextDay.toLocaleDateString('en-CA'));
    } else {
      setNextDate(today.toLocaleDateString('en-CA'));
    }

    setStats({ fullDays, totalDays, streak });
  }

  async function loadLeaderboard() {
    setLoading(true);
    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
    const weekStart = weekAgo.toLocaleDateString('en-CA');
    // The two lists are independent, so ask for them at the same time.
    const [all, week] = await Promise.all([
      supabase.rpc('get_leaderboard', { date_from: yearStart, date_to: yearEnd }),
      supabase.rpc('get_leaderboard', { date_from: weekStart, date_to: yearEnd }),
    ]);
    const toRows = (res) => (res.error || !res.data) ? [] : res.data.map(r => ({
      name: r.name, full: Number(r.full_days), total: Number(r.total_days),
    }));
    setTopAllTime(toRows(all));
    setTopWeek(toRows(week));
    setLoading(false);
  }

  function formatDate(dateStr) {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString(
      (lang === 'zh' || lang === 'sc') ? 'zh-TW' : lang === 'es' ? 'es-ES' : 'en-US',
      { month: 'short', day: 'numeric' }
    );
  }

  const medals = ['🥇', '🥈', '🥉'];

  return (
    <div className="page">
      {/* Personal stats */}
      {stats && (
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-number">{stats.streak}</div>
            <div className="stat-label">{t.streak}</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.fullDays}</div>
            <div className="stat-label">{t.fullDays}</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.totalDays}</div>
            <div className="stat-label">{t.daysRead}</div>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="home-buttons">
        {nextDate && (
          <button
            className="home-btn home-btn-primary"
            onClick={() => navigate(
              nextDate === new Date().toLocaleDateString('en-CA')
                ? '/reading'
                : `/reading?date=${nextDate}`
            )}>
            <div className="home-btn-title">{t.continueReading}</div>
            <div className="home-btn-sub">{t.next} {formatDate(nextDate)}</div>
          </button>
        )}
        <button className="home-btn home-btn-green" onClick={() => navigate('/reading')}>
          <div className="home-btn-title">{t.todayReading}</div>
          <div className="home-btn-sub">{t.goToDate}</div>
        </button>
        <button className="home-btn home-btn-blue" onClick={() => navigate('/schedule')}>
          <div className="home-btn-title">{t.viewSchedule}</div>
          <div className="home-btn-sub">{t.calList}</div>
        </button>
      </div>

      {/* Leaderboards */}
      {loading ? (
        <div className="loading">{t.loading}</div>
      ) : (
        <div className="leaderboards">
          <div className="leaderboard-card">
            <div className="leaderboard-title">{t.thisWeek}</div>
            {topWeek.length === 0
              ? <div className="lb-empty">{t.noReadings}</div>
              : topWeek.map((r, i) => (
                <div className="lb-row" key={`${r.name}-${i}`}>
                  <span className="lb-rank">{medals[i] || i + 1}</span>
                  <span className="lb-name">{r.name}</span>
                  <span className="lb-score">{r.full} {t.full} · {r.total} {t.days}</span>
                </div>
              ))
            }
          </div>
          <div className="leaderboard-card">
            <div className="leaderboard-title">{t.allTime}</div>
            {topAllTime.map((r, i) => (
              <div className="lb-row" key={`${r.name}-${i}`}>
                <span className="lb-rank">{medals[i] || i + 1}</span>
                <span className="lb-name">{r.name}</span>
                <span className="lb-score">{r.full} {t.full} · {r.total} {t.days}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent comments */}
      {recentComments.length > 0 && (
        <div className="recent-comments-section">
          <div className="comments-title">{t.recentComments}</div>
          {recentComments.map((thread, i) => (
            <HomeThreadCard
              key={thread.rootId || i}
              threadComments={thread.comments}
              lang={lang}
              onNavigate={() => {
                const root = thread.comments.find(c => !c.parent_id);
                if (root) navigate(`/reading?date=${root.date}`);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}